'use client';

import Link from 'next/link';
import { DISPLAY_SUBSCRIPTION_PRICE_USD, DISPLAY_SUITE_PRICE_USD } from '@/lib/pricing';
import { LANDING_FAQ } from '@/lib/landing-faq';
import { Eyebrow, Check, ShooshMark, TwitchIcon, YouTubeIcon, TikTokIcon } from './ui';

/* ─────────────────────────────────────────────────────────────────────────
   GROW YOUR CHANNEL — links hub · announcements + commands · merch
   ──────────────────────────────────────────────────────────────────────── */
// Fake-but-convincing QR — three finder eyes (111/101/111 corners) + data.
const QR9 = [
  '111010111',
  '101001101',
  '111010111',
  '000101000',
  '101011101',
  '010100010',
  '111011000',
  '101100101',
  '111010011',
];
function QrMock({ px = 52 }) {
  return (
    <div className="shrink-0 rounded bg-white p-1" style={{ width: px, height: px }}>
      <div
        className="grid h-full w-full"
        style={{ gridTemplateColumns: 'repeat(9, 1fr)', gridTemplateRows: 'repeat(9, 1fr)' }}
      >
        {QR9.join('')
          .split('')
          .map((c, i) => (
            <span key={i} style={{ background: c === '1' ? '#000' : 'transparent' }} />
          ))}
      </div>
    </div>
  );
}

function HeartSm() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 21s-7-4.35-9.5-8.5C.5 9 2 5.5 5.5 5.5c2 0 3.5 1.5 4 2.5 .5-1 2-2.5 4-2.5C19 5.5 21 9 21.5 12.5 19 16.65 12 21 12 21z" />
    </svg>
  );
}

export function Grow() {
  return (
    <section id="grow" className="px-6 py-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <Eyebrow>Grow your channel</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Point viewers <span className="gradient-text">anywhere.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            A link-in-bio page, on-stream promos that fire themselves, chat commands that answer for you — and a
            scan-to-shop merch shelf. The simplest way to monetize every stream.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 stagger items-stretch">
          {/* LEFT — Links hub (link-in-bio) */}
          <div className="flex flex-col rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-6 reveal">
            <div className="text-sm font-bold mb-1">Links hub</div>
            <p className="text-xs text-gum-gray-500 mb-4">One link for everything you share.</p>
            <div className="flex flex-1 flex-col justify-between rounded-xl border border-gum-gray-800 bg-gum-gray-950 p-4">
              <div className="flex flex-col items-center text-center">
                <div
                  className="h-14 w-14 rounded-full bg-gum-gray-700 ring-2 ring-gum-pink/50"
                  style={{
                    backgroundImage: 'url(/landing/merch-face.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <div className="mt-2 text-sm font-black">NyxPlays</div>
                <div className="text-[11px] text-gum-gray-500">shooshlive.com/nyx</div>
                <div className="mt-2 flex items-center gap-2.5">
                  <TwitchIcon size={15} />
                  <YouTubeIcon size={15} />
                  <TikTokIcon size={15} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-1.5 rounded-lg bg-gum-pink py-2 text-xs font-bold text-black shadow-[3px_3px_0_0_#9146ff]">
                  <HeartSm /> Tip me
                </div>
                <div className="rounded-lg border border-gum-gray-700 py-2 text-center text-xs font-semibold text-gum-gray-300">
                  Discord
                </div>
                <div className="rounded-lg border border-gum-gray-700 py-2 text-center text-xs font-semibold text-gum-gray-300">
                  Merch store
                </div>
              </div>
              <div>
                <div className="mb-1.5 flex items-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-gum-pink" />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-gum-gray-500">
                    Stream schedule
                  </span>
                </div>
                <div className="space-y-1">
                  {[
                    ['Mon', '7:00 PM'],
                    ['Wed', '7:00 PM'],
                    ['Sat', '3:00 PM'],
                  ].map(([day, time]) => (
                    <div
                      key={day}
                      className="flex items-center justify-between rounded-md border border-gum-gray-800 bg-gum-gray-900/50 px-2.5 py-1"
                    >
                      <span className="text-[11px] font-semibold text-gum-gray-300">{day}</span>
                      <span className="text-[11px] text-gum-gray-500">{time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* MIDDLE — Announcements + Commands, stacked */}
          <div className="flex flex-col gap-5 reveal">
            {/* Announcements — on-screen auto-promo */}
            <div className="rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-6">
              <div className="text-sm font-bold mb-1">Announcements</div>
              <p className="text-xs text-gum-gray-500 mb-4">On-screen promos that fire themselves.</p>
              <div className="relative rounded-xl border border-gum-gray-800 bg-gum-gray-950 p-3">
                <span className="absolute right-2 top-2 text-[8px] font-bold uppercase tracking-wider text-gum-gray-600">
                  On stream
                </span>
                <div
                  className="flex items-center gap-3 rounded-lg border border-gum-pink/40 p-3"
                  style={{ background: '#181018' }}
                >
                  <QrMock px={48} />
                  <div>
                    <div className="flex items-center gap-1 text-sm font-black text-gum-pink">
                      <HeartSm /> Support the stream
                    </div>
                    <div className="mt-0.5 text-[11px] text-gum-gray-400">Scan to tip — every dono reads out loud</div>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-gum-gray-500">
                Auto-shows when chat&apos;s active · rotates Discord, follow, merch…
              </p>
            </div>

            {/* Commands — trigger → the card appears */}
            <div className="rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-6">
              <div className="text-sm font-bold mb-1">Custom commands</div>
              <p className="text-xs text-gum-gray-500 mb-4">A viewer types it — the card appears.</p>
              <div className="rounded-xl border border-gum-gray-800 bg-gum-gray-950 p-4">
                <div className="flex items-center gap-2 text-sm">
                  <span className="h-2 w-2 rounded-full" style={{ background: '#9146ff' }} />
                  <span className="font-bold" style={{ color: '#a970ff' }}>
                    chatter_42
                  </span>
                  <span className="font-mono text-gum-pink">!discord</span>
                </div>
                <div className="my-2 flex justify-center text-gum-gray-600">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-gum-pink/40 bg-gum-pink/[0.07] p-3">
                  <QrMock px={44} />
                  <div>
                    <div className="text-sm font-black text-white">Join the Discord</div>
                    <div className="text-[11px] text-gum-gray-400">discord.gg/nyx</div>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {['!socials', '!youtube', '!schedule', '!uptime'].map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-gum-gray-700 px-2 py-0.5 font-mono text-[10px] text-gum-gray-400"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Merch (media-paired QR: photo + scan-to-shop) */}
          <div className="flex flex-col rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-6 reveal">
            <div className="text-sm font-bold mb-1">Merch</div>
            <p className="text-xs text-gum-gray-500 mb-4">Show it off — viewers scan to shop.</p>
            <div className="flex flex-1 flex-col rounded-xl border border-gum-gray-800 bg-gum-gray-950 p-4">
              {/* "photo" frame — real merch shot; the media that pairs with the QR */}
              <div
                className="relative flex-1 overflow-hidden rounded-lg border border-gum-gray-800 bg-gum-gray-800"
                style={{
                  minHeight: 200,
                  backgroundImage: 'url(/landing/merch-model.webp)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center 20%',
                }}
              >
                <span className="absolute left-2 top-2 rounded-full bg-black/45 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gum-gray-200 backdrop-blur-sm">
                  New drop
                </span>
              </div>
              {/* QR below the photo */}
              <div className="mt-3 flex items-center gap-3 rounded-lg border border-gum-pink/40 bg-gum-pink/[0.07] p-3">
                <QrMock px={48} />
                <div>
                  <div className="text-sm font-black text-white">Your Merch</div>
                  <div className="text-[11px] text-gum-gray-400">Scan to shop · $28</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   MORE FEATURES — the long-tail grid
   ──────────────────────────────────────────────────────────────────────── */
function FeatureCard({ title, desc, d }) {
  return (
    <div className="rounded-xl border border-gum-gray-800 bg-gum-gray-900/50 p-6 hover:border-gum-pink/50 hover:-translate-y-0.5 transition-all reveal">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-gum-pink text-black">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d={d} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h3 className="font-bold text-lg mb-2">{title}</h3>
      <p className="text-gum-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

const MORE_FEATURES = [
  {
    title: 'Question Queue',
    desc: 'Take viewer Q&A in a dedicated queue. Read one, TTS pauses while you answer, resumes when you’re done.',
    d: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  },
  {
    title: 'Mod Tools',
    desc: '#tts-mute, #tts-skip, #tts-clear. Your mods keep chat audio clean without touching your dashboard.',
    d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  },
  {
    title: '3 platforms at once',
    desc: 'Twitch, YouTube, and TikTok in one queue, one overlay, one set of controls.',
    d: 'M4 6h16M4 12h16M4 18h10',
  },
  {
    title: 'No install',
    desc: 'Runs in your browser. Paste one overlay URL into OBS and you’re live — nothing to download.',
    d: 'M21 15a2 2 0 01-2 2H5a2 2 0 01-2-2M12 3v12m0 0l-4-4m4 4l4-4',
  },
  {
    title: 'Report a Problem',
    desc: 'Something off? Send a report with a full diagnostic log attached — not a ticket into the void.',
    d: 'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  },
  {
    title: 'Nightly backups',
    desc: 'Your settings are backed up every night. Your config is safe, always.',
    d: 'M21 12a9 9 0 11-3-6.7M21 3v6h-6',
  },
];

export function MoreFeatures() {
  return (
    <section id="features" className="px-6 py-24 bg-gum-gray-900/40 border-y border-gum-gray-800">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 reveal">
          <Eyebrow>And the rest</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight">Everything else your stream needs.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 stagger">
          {MORE_FEATURES.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   COMING FROM STREAMELEMENTS — refugee capture
   ──────────────────────────────────────────────────────────────────────── */
const SE_KEEP = [
  'Stream alerts',
  'Donations & tips',
  'OBS overlays',
  'Sounds & SFX',
  'Chat commands',
  'Twitch · YouTube · TikTok',
];
const SE_GAIN = [
  'Voice Hold — TTS that pauses when you talk',
  'Heckle Crowd — chat on stage as characters',
  'A chat arcade — points, character pulls, a co-op siege',
  'A live command center',
  'Per-alert screen-shake & particles',
  'Question Queue for Q&A',
  'Diagnostics that actually help',
];

export function FromStreamElements() {
  return (
    <section id="streamelements" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <Eyebrow>Switching is easy</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            Coming from <span className="gradient-text">StreamElements?</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            You won’t lose a thing — and you’ll gain the stuff they never built. Set up takes about 5 minutes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-gum-gray-800 bg-gum-gray-900/50 p-7 reveal-left">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gum-gray-400 mb-5">Everything you keep</h3>
            <ul className="space-y-3">
              {SE_KEEP.map((k) => (
                <li key={k} className="flex items-center gap-3 text-sm text-gum-gray-200">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gum-gray-700 text-white">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {k}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-gum-pink/30 bg-gum-pink/[0.05] p-7 reveal-right">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gum-pink mb-5">Everything SE never had</h3>
            <ul className="space-y-3">
              {SE_GAIN.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm text-white">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gum-pink text-black">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {g}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-center mt-10 reveal">
          <Link
            href="/login?mode=signup"
            className="inline-flex items-center justify-center font-bold rounded-lg px-7 py-3.5 text-lg bg-gum-pink text-black hover:brightness-110 active:scale-[0.98] transition-all"
          >
            Make the switch — free for 7 days
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   PRICING — two tiers (Stream Suite / Suite + Voice) + credit packs.
   Both CTAs route to /login?mode=signup — every new user gets a 7-day
   full-access trial, then picks a tier in the dashboard Billing panel (where
   the real two-tier checkout lives). Prices come from lib/pricing.js
   (DISPLAY_SUITE_PRICE_USD / DISPLAY_SUBSCRIPTION_PRICE_USD).
   ──────────────────────────────────────────────────────────────────────── */
const SUITE_FEATURES = [
  'Stream alerts — all 3 platforms',
  'Donations + your tip page',
  'Links hub (link-in-bio)',
  '80+ sounds & chat FX',
  'Announcements + custom commands',
  'The live command center',
];
const VOICE_FEATURES = [
  'Everything in Stream Suite',
  'Chat-to-speech TTS',
  'Voice Hold (VAD)',
  'Per-viewer voices',
  '500K characters / month',
  'Credit packs (never expire)',
];

export function Pricing() {
  return (
    <section id="pricing" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12 reveal">
          <h2 className="text-4xl md:text-6xl font-black tracking-tight">Simple pricing.</h2>
          <p className="mt-3 text-lg text-gum-gray-400">
            Two plans. 7-day free trial with everything unlocked — TTS included. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto items-start">
          {/* Stream Suite */}
          <div className="rounded-2xl border border-gum-gray-700 bg-gum-gray-900/50 p-7 flex flex-col reveal-left">
            <span className="text-xs font-bold uppercase tracking-widest text-gum-gray-400">Stream Suite</span>
            <div className="flex items-baseline gap-1 mt-2">
              <span className="text-5xl font-black">${DISPLAY_SUITE_PRICE_USD}</span>
              <span className="text-gum-gray-500 font-semibold">/mo</span>
            </div>
            <p className="mt-2 text-sm text-gum-gray-500">The whole suite — without TTS.</p>
            <ul className="space-y-3 text-sm mt-6 mb-8 flex-1">
              {SUITE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="text-gum-gray-400 mt-0.5">
                    <Check className="h-4 w-4" />
                  </span>{' '}
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href="/login?mode=signup"
              className="inline-flex items-center justify-center w-full font-bold rounded-lg px-6 py-3.5 border-2 border-gum-gray-700 text-white hover:border-gum-pink hover:text-gum-pink transition-colors"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Suite + Voice */}
          <div className="relative rounded-2xl border-2 border-gum-pink bg-gum-pink/[0.05] p-7 flex flex-col reveal-right shadow-[0_30px_80px_-50px_rgba(255,144,232,0.6)]">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gum-pink px-3 py-1 text-[11px] font-black uppercase tracking-wider text-black">
              Most popular
            </span>
            <span className="text-xs font-bold uppercase tracking-widest text-gum-pink">Suite + Voice</span>
            <div className="flex items-baseline gap-1 mt-2">
              <span className="text-5xl font-black">${DISPLAY_SUBSCRIPTION_PRICE_USD}</span>
              <span className="text-gum-gray-500 font-semibold">/mo</span>
            </div>
            <p className="mt-2 text-sm text-gum-gray-400">Adds TTS + Voice Hold — the full Shoosh.</p>
            <ul className="space-y-3 text-sm mt-6 mb-6 flex-1">
              {VOICE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="text-gum-pink mt-0.5">
                    <Check className="h-4 w-4" />
                  </span>{' '}
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gum-gray-500 mb-4">
              Need more characters? Credit packs from $3 — they never expire.
            </p>
            <Link
              href="/login?mode=signup"
              className="inline-flex items-center justify-center w-full font-bold rounded-lg px-6 py-3.5 bg-gum-pink text-black hover:brightness-110 active:scale-[0.98] transition-all"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   FAQ
   ──────────────────────────────────────────────────────────────────────── */
function FaqItem({ q, a }) {
  return (
    <details className="border-b border-gum-gray-800 group reveal">
      <summary className="flex items-center justify-between py-5 cursor-pointer font-bold text-lg list-none">
        {q}
        <span className="text-gum-pink text-2xl group-open:rotate-45 transition-transform">+</span>
      </summary>
      <p className="pb-5 text-gum-gray-400 leading-relaxed">{a}</p>
    </details>
  );
}

export function Faq() {
  return (
    <section id="faq" className="px-6 py-24">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-3xl font-black mb-10 reveal">Questions.</h2>
        <div className="border-t border-gum-gray-800">
          {LANDING_FAQ.map((f) => (
            <FaqItem key={f.q} {...f} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   FINAL CTA + FOOTER
   ──────────────────────────────────────────────────────────────────────── */
export function FinalCta({ viewerEnabled = false }) {
  return (
    <>
      <section className="px-6 py-24">
        <div className="max-w-4xl mx-auto text-center reveal-scale">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">Run your next stream from one tab.</h2>
          <p className="text-gum-gray-400 mb-8">7-day free trial. No commitment. Set up in under 5 minutes.</p>
          <Link
            href="/login?mode=signup"
            className="inline-flex items-center justify-center font-bold rounded-lg px-8 py-4 text-lg bg-gum-pink text-black hover:brightness-110 active:scale-[0.98] transition-all"
          >
            Start Free Trial
          </Link>
        </div>
      </section>
      <footer className="border-t border-gum-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <ShooshMark height={30} />
          <div className="flex items-center gap-6 text-sm text-gum-gray-500">
            <Link href="/commands" className="hover:text-gum-pink transition-colors">
              Commands
            </Link>
            {viewerEnabled && (
              <Link href="/viewer" className="hover:text-gum-pink transition-colors">
                Collection
              </Link>
            )}
            <Link href="/blog" className="hover:text-gum-pink transition-colors">
              Blog
            </Link>
            <Link href="/terms" className="hover:text-gum-pink transition-colors">
              Terms
            </Link>
            <Link href="/privacy" className="hover:text-gum-pink transition-colors">
              Privacy
            </Link>
            <span>© 2026</span>
          </div>
        </div>
      </footer>
    </>
  );
}
